import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const AdvancedSearch = ({ onSearch, onFilterChange, filters = {} }) => {
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const { isAuthenticated } = useAuth();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    name: '',
    employeeId: '',
    department: '',
    position: '',
    location: '',
    hireStartDate: '',
    hireEndDate: '',
    status: '',
    leaveType: '',
    minSalary: '',
    maxSalary: '',
    ...filters
  });

  const employmentStatuses = [
    'active', 'inactive', 'terminated'
  ];

  const leaveTypes = [
    'vacation', 'sick_leave', 'maternity_leave', 'paternity_leave', 'medical_leave',
    'personal_leave', 'bereavement', 'military_leave', 'workers_comp', 
    'disability_leave', 'unpaid_leave', 'other'
  ];

  const handleFilterChange = (field, value) => {
    const updatedFilters = { ...searchFilters, [field]: value };
    setSearchFilters(updatedFilters);
    onFilterChange && onFilterChange(updatedFilters);
    // Trigger search immediately when filters change for real-time results
    onSearch && onSearch(updatedFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      name: '',
      employeeId: '',
      department: '',
      position: '',
      location: '',
      hireStartDate: '',
      hireEndDate: '',
      status: '',
      leaveType: '',
      minSalary: '',
      maxSalary: ''
    };
    setSearchFilters(clearedFilters);
    onFilterChange && onFilterChange(clearedFilters);
    // Trigger search immediately when filters are cleared
    onSearch && onSearch(clearedFilters);
  };

  const activeFilterCount = Object.values(searchFilters).filter(value => 
    value !== '' && value !== false && value !== null && value !== undefined
  ).length;

  const fetchDepartments = useCallback(async () => {
    try {
      // Retrieve all departments from the "positions" table
      const [positionRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/positions`,
          {
            withCredentials: true
          }
        ),
      ]);

      // Extract the department names in alphabetical order, and ensure there are no duplicates
      const departmentNames = Object.values(
        positionRes.data.reduce((departmentMap, position) => {
          const key = position.department.toLowerCase();
          if (!departmentMap[key]) {
            departmentMap[key] = position.department;
          }
          return departmentMap;
        }, {})
      ).sort((a, b) => a.localeCompare(b));

      setDepartments(departmentNames || []);
    } catch (err) {
      console.error('Error fetching departments for filter:', err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const [locationRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/locations`,
          {
            withCredentials: true
          }
        ),
      ]);

      const locationNames = Object.values(
        locationRes.data.reduce((locationMap, location) => {
          const key = location.name.toLowerCase();
          if (!locationMap[key]) {
            locationMap[key] = location.name;
          }
          return locationMap;
        }, {})
      ).sort((a, b) => a.localeCompare(b));

      setLocations(locationNames || []);
    } catch (err) {
      console.error('Error fetching locations for filter:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDepartments();
      fetchLocations();
    }
  }, [isAuthenticated, fetchDepartments, fetchLocations]);

  return (
    <div className="bg-white shadow rounded-lg mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-medium text-gray-900">Employee Search & Analysis</h3>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
              </span>
            )}
          </div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Advanced Filters
            <ChevronDownIcon className={`h-4 w-4 ml-2 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Basic Search */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name
            </label>
            <input
              type="text"
              value={searchFilters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search by name..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee ID
            </label>
            <input
              type="text"
              value={searchFilters.employeeId}
              onChange={(e) => handleFilterChange('employeeId', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="EMP001..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select
              value={searchFilters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employment Status
            </label>
            <select
              value={searchFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              {employmentStatuses.map(status => (
                <option key={status} value={status}>
                  {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

                {/* Filters removed - focusing on core timeline investigation */}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <input
                type="text"
                value={searchFilters.position}
                onChange={(e) => handleFilterChange('position', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search position title..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <select
                value={searchFilters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leave Type
              </label>
              <select
                value={searchFilters.leaveType}
                onChange={(e) => handleFilterChange('leaveType', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Leave Types</option>
                {leaveTypes.map(type => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date From
              </label>
              <input
                type="date"
                value={searchFilters.hireStartDate}
                onChange={(e) => handleFilterChange('hireStartDate', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date To
              </label>
              <input
                type="date"
                value={searchFilters.hireEndDate}
                onChange={(e) => handleFilterChange('hireEndDate', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Salary
                </label>
                <input
                  type="number"
                  value={searchFilters.minSalary}
                  onChange={(e) => handleFilterChange('minSalary', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="25000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Salary
                </label>
                <input
                  type="number"
                  value={searchFilters.maxSalary}
                  onChange={(e) => handleFilterChange('maxSalary', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="100000"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Filters Button */}
      {activeFilterCount > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6 flex justify-center">
          <button
            onClick={clearFilters}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <XMarkIcon className="h-4 w-4 mr-2" />
            Clear Filters ({activeFilterCount})
          </button>
        </div>
      )}


    </div>
  );
};

export default AdvancedSearch; 